import { GamepadActionId } from 'generated/graphql';
import type { DictaphoneKeyMapping, DictaphoneProduct } from './types';
import { parseUsbIdsFromLabel } from '../usbDeviceLabel';

const SPEECHMIKE_III_BUTTONS = [
  { name: 'Instruction', keyCode: 9 },
  { name: 'EOL/PRIO', keyCode: 14 },
  { name: 'INS/OVR', keyCode: 4 },
  { name: 'Rewind', keyCode: 0 },
  { name: 'Record', keyCode: 5 },
  { name: 'Play/Pause', keyCode: 1 },
  { name: 'Fast Forward', keyCode: 2 },
  { name: 'F1', keyCode: 10 },
  { name: 'F2', keyCode: 11 },
  { name: 'F3', keyCode: 12 },
  { name: 'F4', keyCode: 13 },
  { name: 'Rear 1', keyCode: 6 },
  { name: 'Rear 2', keyCode: 7 },
] as const;

export const SPEECHMIKE_III_DEADMAN_SWITCH_KEYCODE = '8';
export const SPEECHMIKE_III_DEFAULT_KEY_MAPPING = {
  '0': GamepadActionId.Back,
  '5': GamepadActionId.Record,
  '2': GamepadActionId.Forward,
  '14': GamepadActionId.SubmitReport,
  '7': GamepadActionId.ToggleFocusMode,
  // Deadman switch defaults to command mode calibration
  [SPEECHMIKE_III_DEADMAN_SWITCH_KEYCODE]: GamepadActionId.CommandMode,
} as const satisfies DictaphoneKeyMapping;

const SPEECHMIKE_III_VENDOR_ID = 0x0911;
const SPEECHMIKE_III_PRODUCT_IDS = new Set([0x0c1c, 0x0c1d, 0x0c80, 0x0e74, 0x0fa0]);
const SPEECHMIKE_MATCH_TOKEN = 'speechmike iii';

export enum SpeechMikeIIIWebHIDValueMap {
  BACK = 16,
  RECORD = 1,
  FORWARD = 8,
  SUBMIT_REPORT = 32,
  COMMAND_MODE = 2,
  TOGGLE_FOCUS_MODE = 8192,
}

const SPEECHMIKE_III_WEBHID_BINDINGS = [
  { value: SpeechMikeIIIWebHIDValueMap.RECORD, action: GamepadActionId.Record },
  { value: SpeechMikeIIIWebHIDValueMap.COMMAND_MODE, action: GamepadActionId.CommandMode },
  { value: SpeechMikeIIIWebHIDValueMap.FORWARD, action: GamepadActionId.Forward },
  { value: SpeechMikeIIIWebHIDValueMap.BACK, action: GamepadActionId.Back },
  { value: SpeechMikeIIIWebHIDValueMap.SUBMIT_REPORT, action: GamepadActionId.SubmitReport },
  { value: SpeechMikeIIIWebHIDValueMap.TOGGLE_FOCUS_MODE, action: GamepadActionId.ToggleFocusMode },
] as const;

export const SPEECHMIKE_III_WEBHID_ACTION_MAP = Object.fromEntries(
  SPEECHMIKE_III_WEBHID_BINDINGS.map(({ value, action }) => [value, action])
) as Record<SpeechMikeIIIWebHIDValueMap, GamepadActionId>;

export const SPEECHMIKE_III_PRODUCT: DictaphoneProduct = {
  id: 'speechmike-iii',
  displayName: 'Philips SpeechMike III',
  matchesDeviceFingerprint: ({ deviceLabel, productName, vendorId, productId }) => {
    // Saved custom mappings may contain only the audio label, including its USB suffix.
    const usbIds =
      vendorId != null && productId != null
        ? { vendorId, productId }
        : parseUsbIdsFromLabel(deviceLabel ?? '');
    if (usbIds != null) {
      if (
        usbIds.vendorId === SPEECHMIKE_III_VENDOR_ID &&
        SPEECHMIKE_III_PRODUCT_IDS.has(usbIds.productId)
      ) {
        return true;
      }
    }

    const label = deviceLabel?.toLowerCase() ?? '';
    const name = productName?.toLowerCase() ?? '';
    return label.includes(SPEECHMIKE_MATCH_TOKEN) || name.includes(SPEECHMIKE_MATCH_TOKEN);
  },
  defaultKeyMapping: SPEECHMIKE_III_DEFAULT_KEY_MAPPING,
  buttons: SPEECHMIKE_III_BUTTONS,
  parseWebHIDReport: ({ currentValue, previousValue }) => {
    // Compare the previous and current bitmasks so we emit one pressed/released event per changed button.
    const priorValue = previousValue ?? 0;
    const changedMask = priorValue ^ currentValue;
    const releasedEvents = SPEECHMIKE_III_WEBHID_BINDINGS.filter(
      ({ value }) => (changedMask & value) !== 0 && (currentValue & value) === 0
    ).map(({ value }) => ({ value, pressed: false as const }));

    const pressedEvents = SPEECHMIKE_III_WEBHID_BINDINGS.filter(
      ({ value }) => (changedMask & value) !== 0 && (currentValue & value) !== 0
    ).map(({ value }) => ({ value, pressed: true as const }));

    return [...releasedEvents, ...pressedEvents];
  },
};
